/**
 * Examples:
 * - default: 1000.101 -> 1000.10
 * - integer: 1000.10 -> 1000
 * - float: 1000 -> 1000.00
 * - short: 1000 -> 1k
 */
export type NumberFormat = 'default' | 'integer' | 'float' | 'short';
export type Formatter = (unicodeLangString: string, currency?: string) => Intl.NumberFormat;

/**
 * Some currency have own number formatting rules, like EUR,
 * for example:
 * - new Intl.NumberFormat('de-AT', { style: 'decimal' }).format(1000) -> 1 000
 * - new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(1000.10) -> 1.000
 * For this reason we need to use custom formatter for each currency,
 * that will use number format from country code, not the currency format
 */
function stableFormat(unicodeLangString: string, options: Exclude<Intl.NumberFormatOptions, 'style'>) {
  const dec = new Intl.NumberFormat(unicodeLangString, {
    style: 'decimal',
    ...options,
  });

  if (!options.currency) return dec;

  const cur = new Intl.NumberFormat(unicodeLangString, {
    style: 'currency',
    ...options,
  });

  const getParts = (value: number) => {
    const { group, decimal } = dec.formatToParts(value).reduce(
      (acc, p) => {
        if (p.type === 'group') {
          acc.group = p.value;
        }
        if (p.type === 'decimal') {
          acc.decimal = p.value;
        }
        return acc;
      },
      { group: '', decimal: '' },
    );

    return cur.formatToParts(value).map((p) => {
      if (p.type === 'group') return { ...p, value: group };
      if (p.type === 'decimal') return { ...p, value: decimal };
      return p;
    });
  };
  return {
    ...cur,
    format: (value: number) => {
      const parts = getParts(value);
      let result = '';

      for (let i = 0; i < parts.length; i += 1) {
        const part = parts[i];
        const nextPart = parts[i + 1];

        if (!part) continue;

        result += part.value;

        // Ensure consistent spacing between currency and number
        if (part.type === 'currency' && nextPart && (nextPart.type === 'integer' || nextPart.type === 'minusSign')) {
          // Add space if not already present
          if (!part.value.endsWith(' ') && !nextPart.value.startsWith(' ')) {
            result += ' ';
          }
        }
      }

      return result;
    },
    formatToParts: (value: number) => getParts(value),
  };
}

const formatters: Record<NumberFormat, Formatter> = {
  default: (unicodeLangString: string, currency?: string) =>
    stableFormat(unicodeLangString, {
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }),
  integer: (unicodeLangString: string, currency?: string) =>
    stableFormat(unicodeLangString, {
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  short: (unicodeLangString: string, currency?: string) =>
    stableFormat(unicodeLangString, {
      currency,
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    }),
  float: (unicodeLangString: string, currency?: string) =>
    stableFormat(unicodeLangString, {
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),
};

export const getFormatter = (() => {
  const cache = new Map<string, Intl.NumberFormat>();
  const key = (...args: string[]) => args.join('-');
  return (format: NumberFormat, country: string, language: string, currency?: string) => {
    const unicodeLangString = `${language.toLocaleLowerCase()}-${country.toUpperCase()}`;
    const k = key(format, unicodeLangString, currency ?? '-');
    if (!cache.has(k)) {
      const f = formatters[format](unicodeLangString, currency);
      cache.set(k, f);
    }
    return cache.get(k)!;
  };
})();
